import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  RiText,
  RiImageLine,
  RiVideoLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiSaveLine,
  RiFolderOpenLine,
  RiPlayLine,
  RiStopLine,
  RiZoomInLine,
  RiZoomOutLine,
  RiGroupFill,
  RiPaletteLine,
  RiLockLine,
  RiLockUnlockLine,
} from '@remixicon/react';
import {
  addElement,
  deleteElement,
  duplicateElement,
  reorderElement,
  setZoom,
  setIsPlaying,
  saveProject,
  loadProject,
  setProjectName,
} from '../redux/slices/canvasSlice';

const Toolbar = ({ onOpenBackgroundSelector, onOpenExportDialog }) => {
  const dispatch = useDispatch();
  const { selectedElementId, elements, zoom, isPlaying, projectName } = useSelector(
    (state) => state.canvas
  );
  
  const [layersMenuAnchor, setLayersMenuAnchor] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [tempProjectName, setTempProjectName] = useState(projectName);
  
  const selectedElement = elements.find((el) => el.id === selectedElementId);
  
  const handleAddText = () => {
    dispatch(
      addElement({
        type: 'text',
        properties: {
          text: 'Double click to edit',
          fontSize: '24px',
          fontFamily: 'Outfit',
          color: '#000000',
          textAlign: 'left',
        },
      })
    );
  };
  
  const handleAddImage = () => {
    // In a real app, this would open a file picker or asset library
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl) {
      dispatch(
        addElement({
          type: 'image',
          properties: {
            src: imageUrl,
            objectFit: 'cover',
          },
          size: { width: 300, height: 200 },
        })
      );
    }
  };
  
  const handleAddVideo = () => {
    // In a real app, this would open a file picker or asset library
    const videoUrl = prompt('Enter video URL:');
    if (videoUrl) {
      dispatch(
        addElement({
          type: 'video',
          properties: {
            src: videoUrl,
            autoPlay: true,
            loop: true,
            muted: true,
            objectFit: 'cover',
          },
          size: { width: 400, height: 300 },
        })
      );
    }
  };
  
  const handleDelete = () => {
    if (selectedElementId) {
      dispatch(deleteElement(selectedElementId));
    }
  };
  
  const handleDuplicate = () => {
    if (selectedElementId) {
      dispatch(duplicateElement(selectedElementId));
    }
  };
  
  const handleZoomIn = () => {
    dispatch(setZoom(Math.min(zoom + 0.1, 2)));
  };
  
  const handleZoomOut = () => {
    dispatch(setZoom(Math.max(zoom - 0.1, 0.1)));
  };
  
  const handleTogglePlay = () => {
    dispatch(setIsPlaying(!isPlaying));
  };
  
  const handleSaveProject = () => {
    setSaveDialogOpen(true);
  };
  
  const handleConfirmSave = () => {
    // Get current canvas state
    const canvasState = {
      projectName: tempProjectName,
      ...elements,
    };
    
    // Convert to JSON and trigger download
    const json = JSON.stringify(canvasState, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${tempProjectName.replace(/\s+/g, '-').toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    dispatch(setProjectName(tempProjectName));
    dispatch(saveProject());
    setSaveDialogOpen(false);
  };
  
  const handleLoadProject = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const projectData = JSON.parse(event.target.result);
            dispatch(loadProject(projectData));
          } catch (error) {
            alert('Failed to load project: Invalid JSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };
  
  const handleLayerAction = (action) => {
    if (selectedElementId) {
      dispatch(reorderElement({ id: selectedElementId, direction: action }));
    }
    setLayersMenuAnchor(null);
  };
  
  return (
    <>
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 1,
          gap: 1,
          backgroundColor: 'white',
          borderRadius: 2,
        }}
      >
        {/* Add Elements */}
        <Tooltip title="Add Text">
          <IconButton onClick={handleAddText} size="small">
            <RiText size={20} />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Add Image">
          <IconButton onClick={handleAddImage} size="small">
            <RiImageLine size={20} />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Add Video">
          <IconButton onClick={handleAddVideo} size="small">
            <RiVideoLine size={20} />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem />
        
        {/* Element Actions */}
        <Tooltip title="Duplicate">
          <span>
            <IconButton
              onClick={handleDuplicate}
              size="small"
              disabled={!selectedElementId}
            >
              <RiFileCopyLine size={20} />
            </IconButton>
          </span>
        </Tooltip>
        
        <Tooltip title="Delete">
          <span>
            <IconButton
              onClick={handleDelete}
              size="small"
              disabled={!selectedElementId}
              color="error"
            >
              <RiDeleteBinLine size={20} />
            </IconButton>
          </span>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem />
        
        {/* Layers */}
        <Tooltip title="Layers">
          <span>
            <IconButton
              onClick={(e) => setLayersMenuAnchor(e.currentTarget)}
              size="small"
              disabled={!selectedElementId}
            >
              <RiGroupFill size={20} />
            </IconButton>
          </span>
        </Tooltip>
        
        {/* Background */}
        <Tooltip title="Background">
          <IconButton onClick={onOpenBackgroundSelector} size="small">
            <RiPaletteLine size={20} />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem />
        
        {/* Zoom Controls */}
        <Tooltip title="Zoom Out">
          <IconButton onClick={handleZoomOut} size="small">
            <RiZoomOutLine size={20} />
          </IconButton>
        </Tooltip>
        
        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        
        <Tooltip title="Zoom In">
          <IconButton onClick={handleZoomIn} size="small">
            <RiZoomInLine size={20} />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem />
        
        {/* Preview */}
        <Tooltip title={isPlaying ? 'Stop Preview' : 'Play Preview'}>
          <IconButton onClick={handleTogglePlay} size="small" color="primary">
            {isPlaying ? <RiStopLine size={20} /> : <RiPlayLine size={20} />}
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem />
        
        {/* Save/Load */}
        <Tooltip title="Save Project">
          <IconButton onClick={handleSaveProject} size="small">
            <RiSaveLine size={20} />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Load Project">
          <IconButton onClick={handleLoadProject} size="small">
            <RiFolderOpenLine size={20} />
          </IconButton>
        </Tooltip>
      </Paper>
      
      {/* Layers Menu */}
      <Menu
        anchorEl={layersMenuAnchor}
        open={Boolean(layersMenuAnchor)}
        onClose={() => setLayersMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleLayerAction('top')}>Bring to Front</MenuItem>
        <MenuItem onClick={() => handleLayerAction('up')}>Bring Forward</MenuItem>
        <MenuItem onClick={() => handleLayerAction('down')}>Send Backward</MenuItem>
        <MenuItem onClick={() => handleLayerAction('bottom')}>Send to Back</MenuItem>
      </Menu>
      
      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            type="text"
            fullWidth
            value={tempProjectName}
            onChange={(e) => setTempProjectName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Toolbar;



